import { useState } from 'react';
import { User, Mail, Languages, Globe, Wallet } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';
import { useAuth } from '@/state/auth';
import GetStartedButton from '@/components/shsfui/button/get-started-button';
import { WalletAddressModal } from '@/components/modals/wallet-address-modal';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription,
  DialogTrigger 
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useTranslation as useI18n } from 'react-i18next';

const ProfileSettings = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { i18n } = useI18n();
  const [isLanguageModalOpen, setIsLanguageModalOpen] = useState(false);
  const [isWalletModalOpen, setIsWalletModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    fullName: 'Azusa Nakano',
    email: 'elementary221b@gmail.com',
    phone: '+44 (123) 456-9878',
    accountType: 'Regular'
  });

  const availableLanguages = [
    { code: 'en', name: 'English', nativeName: 'English' },
    { code: 'ar', name: 'Arabic', nativeName: 'العربية' }
  ];

  const currentLanguage = availableLanguages.find(lang => lang.code === i18n.language) || availableLanguages[0];

  const handleLanguageChange = (languageCode: string) => {
    i18n.changeLanguage(languageCode);
    setIsLanguageModalOpen(false);
  };


  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };


  return (
    <div className="min-h-screen p-4">
      <div className="max-w-lg mx-auto">
        {/* Header */}
        <div className="text-center mb-8 pt-8">
          <div className="w-20 h-20 rounded-full mx-auto mb-4 overflow-hidden bg-gray-200">
            <img 
              src="/api/placeholder/80/80" 
              alt="Profile"
              className="w-full h-full object-cover"
            />
          </div>
          <span className="inline-block bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full mb-2">
            {t('pages.profile.basicPlan')}
          </span>
          <p className="text-gray-700 font-medium">#{user?.uid}</p>
          <GetStartedButton className="mt-4 " onClick={() => window.location.href = '/dashboard/network'}>
            {t('pages.profile.viewNetwork')}
          </GetStartedButton>
          {/* <button className="mt-4 bg-primary hover:bg-primary-700 text-primary-foreground px-6 py-2 rounded-lg font-medium transition-colors inline-flex items-center gap-2">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </button> */}
        </div>

        {/* Personal Info Section */}
        <div className="mb-6">
          <h2 className="text-lg font-semibold text-gray-600 mb-2">{t('pages.profile.personalInfo')}</h2>
        </div>

        {/* Form Card */}
        <div className="bg-gray-900 rounded-3xl p-6 space-y-6">
          {/* Full Name */}
          <div>
            <label className="block text-white text-sm font-medium mb-2">
              {t('pages.profile.fullName')}
            </label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                disabled
                type="text"
                value={user?.name}
                onChange={(e) => handleInputChange('fullName', e.target.value)}
                className="w-full bg-gray-800 border border-gray-700 rounded-lg pl-10 pr-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                placeholder={t('pages.profile.enterFullName')}
              />
            </div>
          </div>

          {/* Email Address */}
          <div>
            <label className="block text-white text-sm font-medium mb-2">
              {t('pages.profile.emailAddress')}
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                disabled
                type="email"
                value={"verified email hidden for privacy"}
                onChange={(e) => handleInputChange('email', e.target.value)}
                className="w-full bg-gray-800 border border-gray-700 rounded-lg pl-10 pr-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                placeholder={t('pages.profile.enterEmail')}
              />
            </div>
          </div>

          {/* Phone Number */}
          <div>
            <label className="block text-white text-sm font-medium mb-2">
              {t('pages.profile.telegramUid')}
            </label>
            <div className="flex gap-2">
              
              <input
                type="tel"
                disabled
                value={user?.uid as string}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                className="flex-1 bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                placeholder={t('pages.profile.telegramUid')}
              />
              <button className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-3 text-gray-400 hover:text-white transition-colors">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </button>
            </div>
          </div>

          {/* Account Type */}
          <div>
            <label className="block text-white text-sm font-medium mb-2">
              {t('pages.profile.accountType')}
            </label>
            <div className="relative">
              <select
                disabled
                value={formData.accountType}
                onChange={(e) => handleInputChange('accountType', e.target.value)}
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent appearance-none cursor-pointer"
              >
                <option value="Regular">{t('pages.profile.accountTypes.regular')}</option>
                <option value="Premium">{t('pages.profile.accountTypes.premium')}</option>
                <option value="Business">{t('pages.profile.accountTypes.business')}</option>
              </select>
              <svg className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>

        </div>

        {/* Wallet Address Card */}
        <div className="mb-6 mt-6">
          <h2 className="text-lg font-semibold text-gray-600 mb-2">{t('pages.profile.walletAddress')}</h2>
          
          <div 
            onClick={() => setIsWalletModalOpen(true)}
            className="bg-gray-900 rounded-3xl p-6 cursor-pointer hover:bg-gray-800 transition-colors"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <Wallet className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <h3 className="text-white font-medium">
                    {user?.crypto_wallet_address 
                      ? t('pages.profile.walletLinked') 
                      : t('pages.profile.linkWallet')}
                  </h3>
                  <p className="text-gray-400 text-sm">
                    {user?.crypto_wallet_address 
                      ? `${user.crypto_wallet_address.slice(0, 8)}...${user.crypto_wallet_address.slice(-8)}`
                      : t('pages.profile.walletNotLinked')}
                  </p>
                </div>
              </div>
              <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
          </div>
        </div>

        {/* Language Selection Card */}
        <div className="mb-6 mt-6">
          <h2 className="text-lg font-semibold text-gray-600 mb-2">{t('pages.profile.language')}</h2>
          
          <Dialog open={isLanguageModalOpen} onOpenChange={setIsLanguageModalOpen}>
            <DialogTrigger asChild>
              <div className="bg-gray-900 rounded-3xl p-6 cursor-pointer hover:bg-gray-800 transition-colors">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-primary/10 rounded-lg">
                      <Languages className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <h3 className="text-white font-medium">{t('pages.profile.currentLanguage')}</h3>
                      <p className="text-gray-400 text-sm">{currentLanguage.nativeName}</p>
                    </div>
                  </div>
                  <Globe className="w-5 h-5 text-gray-400" />
                </div>
              </div>
            </DialogTrigger>
            
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="text-white">{t('pages.profile.selectLanguage')}</DialogTitle>
                <DialogDescription className="text-gray-400">
                  {t('pages.profile.selectLanguageDescription')}
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-3 mt-4">
                {availableLanguages.map((language) => (
                  <Button
                    key={language.code}
                    variant="default"
                    className={`w-full justify-start h-auto p-4 ${
                      currentLanguage.code === language.code 
                        ? 'bg-primary text-primary-foreground' 
                        : 'bg-gray-800 text-white hover:bg-gray-700'
                    }`}
                    onClick={() => handleLanguageChange(language.code)}
                  >
                    <div className="flex items-center space-x-3">
                      <Globe className="w-4 h-4" />
                      <div className="text-left">
                        <div className="font-medium">{language.name}</div>
                        <div className="text-sm opacity-75">{language.nativeName}</div>
                      </div>
                      {currentLanguage.code === language.code && (
                        <div className="ml-auto w-2 h-2 bg-current rounded-full" />
                      )}
                    </div>
                  </Button>
                ))}
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Wallet Address Modal */}
        <WalletAddressModal 
          isOpen={isWalletModalOpen}
          onClose={() => setIsWalletModalOpen(false)}
          currentAddress={user?.crypto_wallet_address}
        />

        {/* Action Buttons */}
        
      </div>
    </div>
  );
};

export default ProfileSettings;